/**
 * Portal Projects Page — shows projects assigned to the client user's organization(s)
 * Supports card view (grid) and map view toggle.
 */

import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import { LocalImage } from '../../components/common/LocalImage';
import { ProjectsMapView } from '../../components/portal/ProjectsMapView';
import type { Project } from '../../../shared/types/project';
import type { Client } from '../../../shared/types/auth';
import { formatProjectName } from '../../../shared/formatProjectName';
import './portal-responsive.css';

type ViewMode = 'cards' | 'map';

export function PortalProjectsPage() {
  const { client } = useOutletContext<{ client: Client | null }>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>(() => {
    return (localStorage.getItem('portal_projects_view') as ViewMode) || 'cards';
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    api.portal.getProjects()
      .then(setProjects)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const switchView = (v: ViewMode) => {
    setView(v);
    localStorage.setItem('portal_projects_view', v);
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading projects...</div>;

  if (projects.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <h2 style={{ color: '#f1f5f9', fontSize: 20 }}>No Locations Available</h2>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Locations assigned to your organization will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="portal-page-content" style={{ maxWidth: 'none' }}>
      {client && (
        <div style={styles.welcomeHeader}>
          {client.logoPath ? (
            <img src={client.logoPath} alt={client.name} style={styles.clientLogo} />
          ) : (
            <div style={styles.clientLogoFallback}>
              {client.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={styles.clientName}>{client.name}</div>
            <div style={styles.clientSubtitle}>Project Portal</div>
          </div>
        </div>
      )}

      {/* Header with toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
          Your Locations
        </h1>
        <div style={styles.toggleGroup}>
          <button
            onClick={() => switchView('cards')}
            style={{
              ...styles.toggleBtn,
              ...(view === 'cards' ? styles.toggleBtnActive : {}),
            }}
            title="Card view"
            aria-label="Card view"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => switchView('map')}
            style={{
              ...styles.toggleBtn,
              ...(view === 'map' ? styles.toggleBtnActive : {}),
            }}
            title="Map view"
            aria-label="Map view"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
              <line x1="8" y1="2" x2="8" y2="18" />
              <line x1="16" y1="6" x2="16" y2="22" />
            </svg>
          </button>
        </div>
      </div>

      {/* View content */}
      {view === 'cards' ? (
        <div className="portal-projects-grid">
          {projects.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} style={styles.card}>
              {p.thumbnailPath ? (
                <LocalImage
                  src={p.thumbnailPath}
                  alt={formatProjectName(p, { portal: true })}
                  style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: '12px 12px 0 0' }}
                />
              ) : (
                <div style={{ height: 140, background: '#1a1a1a', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 32 }}>
                  &#9670;
                </div>
              )}
              <div style={styles.cardBody}>
                <h3 style={styles.cardTitle}>{formatProjectName(p, { portal: true })}</h3>
                {(p.city || p.state) && (
                  <div style={styles.cardMeta}>{[p.city, p.state].filter(Boolean).join(', ')}</div>
                )}
                {p.category && <span style={styles.badge}>{p.category}</span>}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <ProjectsMapView projects={projects} />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  welcomeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: '1px solid #1e1e1e',
  },
  clientLogo: {
    height: 48,
    maxWidth: 180,
    objectFit: 'contain' as const,
  },
  clientLogoFallback: {
    width: 48,
    height: 48,
    borderRadius: 10,
    background: '#1e293b',
    color: '#94a3b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 700,
    flexShrink: 0,
  },
  clientName: {
    color: '#f1f5f9',
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  clientSubtitle: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 400,
    marginTop: 2,
  },
  toggleGroup: {
    display: 'flex',
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: 8,
    overflow: 'hidden',
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 36,
    background: 'transparent',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s',
  },
  toggleBtnActive: {
    background: '#334155',
    color: '#f1f5f9',
  },
  card: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'border-color 0.15s',
  },
  cardBody: {
    padding: '16px 20px',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#f1f5f9',
    margin: '0 0 8px',
  },
  cardMeta: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 8,
  },
  badge: {
    display: 'inline-block',
    background: '#333333',
    color: '#94a3b8',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'capitalize' as const,
  },
};
